import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Badge,
  Input,
  Label,
  FormGroup,
  Button,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import HotspotDetailModal from "./helper/hotspotDetailModal";
import RejectionModal from "./helper/rejectionModal";
import SuspendModal from "./helper/suspendModal";
import UserInfoModal from "../../components/Common/UserInfoModal";
import {
  getHotspotModerationList,
  approveHotspot,
  rejectHotspot,
  getTagDetails,
  suspendHotspot,
  updateTagVerification,
} from "./helper/hotspotModerationApi";

const HotspotModerationPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailModal, setDetailModal] = useState(false);
  const [selectedHotspot, setSelectedHotspot] = useState(null);
  const [rejectionModal, setRejectionModal] = useState(false);
  const [hotspotToReject, setHotspotToReject] = useState(null);
  const [suspendModal, setSuspendModal] = useState(false);
  const [hotspotToSuspend, setHotspotToSuspend] = useState(null);
  const [userInfoModal, setUserInfoModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [loadingTagDetails, setLoadingTagDetails] = useState(false);

  // Filter states
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [keyword, setKeyword] = useState("");
  const [dateRange, setDateRange] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isSuspended, setIsSuspended] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(50);
  const [totalRows, setTotalRows] = useState(0);

  // Refs for optimization
  const keywordTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const isLoadingRef = useRef(false);
  const previousFiltersRef = useRef(null);

  const columns = useMemo(
    () => [
      {
        name: <span className="font-weight-bold fs-13">Request ID</span>,
        selector: (row) => row.request_id,
        sortable: true,
        cell: (row) => (
          <span
            style={{ cursor: "pointer", color: "#0d6efd", textDecoration: "underline" }}
            onClick={() => handleRequestIdClick(row)}
          >
            {row.request_id}
          </span>
        ),
      },
      {
        name: <span className="font-weight-bold fs-13">Tag Name</span>,
        selector: (row) => row.tag_name,
        sortable: true,
        wrap: true,
      },
      {
        name: <span className="font-weight-bold fs-13">Type</span>,
        selector: (row) => row.type,
        sortable: true,
        cell: (row) => <Badge color="info">{row.type}</Badge>,
      },
      {
        name: <span className="font-weight-bold fs-13">Review Status</span>,
        selector: (row) => row.review_status,
        sortable: true,
        cell: (row) => {
          const getStatusBadge = (status) => {
            switch (status?.toLowerCase()) {
              case "approved":
                return <Badge color="success">Approved</Badge>;
              case "rejected":
                return <Badge color="danger">Rejected</Badge>;
              case "pending":
              default:
                return <Badge color="warning">Pending</Badge>;
            }
          };
          return getStatusBadge(row.review_status);
        },
      },
      {
        name: <span className="font-weight-bold fs-13">Verified</span>,
        selector: (row) => row.is_verified ?? false,
        sortable: true,
        cell: (row) => {
          const isVerified = row.is_verified === true;
          return (
            <Badge color={isVerified ? "success" : "secondary"}>
              {isVerified ? "True" : "False"}
            </Badge>
          );
        },
      },
      {
        name: <span className="font-weight-bold fs-13">Amount</span>,
        selector: (row) => row.amount || "-",
        sortable: true,
        cell: (row) => (
          <span>
            {row.amount && row.currency
              ? `${row.currency} ${row.amount}`
              : "-"}
          </span>
        ),
      },
      {
        name: <span className="font-weight-bold fs-13">Category Name</span>,
        selector: (row) => row.category_name,
        sortable: true,
        wrap: true,
      },
      {
        name: <span className="font-weight-bold fs-13">Created At</span>,
        selector: (row) => row.createdAt,
        sortable: true,
        cell: (row) => {
          if (!row.createdAt) return "-";
          const date = new Date(row.createdAt);
          return date.toLocaleString();
        },
      },
    ],
    []
  );

  const handleRequestIdClick = useCallback(async (hotspot) => {
    // If approved, fetch tag details from API using tag_id
    if (hotspot.review_status?.toLowerCase() === "approved" && hotspot.tag_id) {
      try {
        setLoadingTagDetails(true);
        setDetailModal(true);
        setSelectedHotspot(null); // Clear while loading
        
        const result = await getTagDetails(hotspot.tag_id);
        const tagData = result?.data?.result;
        
        if (tagData) {
          // Map the tag data to match the modal's expected structure
          const mappedData = {
            request_id: hotspot.request_id, // Keep original request_id
            tag_id: tagData.tag_id,
            tag_name: tagData.tag_name,
            type: tagData.type,
            review_status: "approved",
            location: hotspot.location || "-", // May not be in tag response
            geohash: hotspot.geohash || "-",
            category_id: tagData.category_id || hotspot.category_id,
            category_name: tagData.category_name,
            description: tagData.description,
            image_url: tagData.image_url,
            background_image: tagData.background_image,
            createdAt: tagData.date_created ? new Date(tagData.date_created).toISOString() : hotspot.createdAt,
            // Additional tag-specific fields
            type_id: tagData.type_id,
            is_creator: tagData.is_creator,
            is_following: tagData.is_following,
            is_hidden: tagData.is_hidden,
            followers: tagData.followers,
            Date_expires: tagData.Date_expires,
            last_posted: tagData.last_posted,
            pendingRequests: tagData.pendingRequests,
            expiring_soon: tagData.expiring_soon,
            creator_info: tagData.creator_info,
            // Payment fields (may not be in tag response)
            payment_id: hotspot.payment_id,
            transaction_id: hotspot.transaction_id,
            amount: hotspot.amount,
            currency: hotspot.currency,
            payment_status: hotspot.payment_status,
            payment_method: hotspot.payment_method,
            rejection_reason: null,
            // User info from creator_info or original
            user_id: tagData.creator_info?.[0]?.user_id || hotspot.user_id,
            username: tagData.creator_info?.[0]?.username || hotspot.username,
          };
          setSelectedHotspot(mappedData);
        } else {
          // Fallback to original hotspot data if API fails
          setSelectedHotspot(hotspot);
        }
      } catch (error) {
        console.error("Error fetching tag details:", error);
        // Fallback to original hotspot data on error
        setSelectedHotspot(hotspot);
      } finally {
        setLoadingTagDetails(false);
      }
    } else {
      // For pending/rejected, use the data directly
      setSelectedHotspot(hotspot);
      setDetailModal(true);
    }
  }, []);

  const toggleDetailModal = useCallback(() => {
    setDetailModal((prev) => !prev);
  }, []);

  // Optimized fetchList with request cancellation
  const fetchList = useCallback(async (debouncedKeyword = null) => {
    // Prevent duplicate calls if already loading
    if (isLoadingRef.current) {
      return;
    }

    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Set loading state
    isLoadingRef.current = true;
    setLoading(true);

    try {
      const filters = {
        status: status || undefined,
        type: type || undefined,
        limit,
        page: currentPage,
        keyword: debouncedKeyword !== null ? debouncedKeyword : (keyword || undefined),
        dateRange: dateRange || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        is_suspended: isSuspended !== "" ? isSuspended : undefined,
      };

      const result = await getHotspotModerationList(filters);
      
      // Check if request was aborted or component unmounted
      if (abortController.signal.aborted || !isMountedRef.current) {
        return;
      }
      
      // Extract data and pagination from API response
      const hotspotData = result?.data || [];
      const paginationInfo = result?.pagination || {};
      
      setData(hotspotData);
      setTotalRows(paginationInfo.totalRecords || 0);
    } catch (error) {
      // Don't log aborted requests
      if (error.name !== 'AbortError' && isMountedRef.current) {
        console.error("Error fetching hotspot moderation list:", error);
        setData([]);
        setTotalRows(0);
      }
    } finally {
      isLoadingRef.current = false;
      if (isMountedRef.current && !abortController.signal.aborted) {
        setLoading(false);
      }
      // Clear abort controller if this was the active request
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  }, [status, type, keyword, dateRange, startDate, endDate, isSuspended, currentPage, limit]);

  // Handle keyword change (debounced)
  const handleKeywordChange = useCallback((value) => {
    setKeyword(value);
    setCurrentPage(1);
  }, []);

  const handleApprove = useCallback(async (hotspot) => {
    try {  
      // Call the approve API
      await approveHotspot({ request_id: hotspot.request_id });
      
      // Optimistically remove from list
      setData((prevData) =>
        prevData.filter((item) => item.request_id !== hotspot.request_id)
      );
      setTotalRows((prev) => Math.max(0, prev - 1));
      
      // Refetch to ensure consistency
      await fetchList();
      setDetailModal(false);
      setSelectedHotspot(null);
    } catch (error) {
      console.error("Error approving hotspot:", error);
      // Show error message to user
      alert(error.response?.data?.message || error.message || "Failed to approve hotspot");
      // Refetch on error to restore state
      fetchList();
    }
  }, [fetchList]);

  const handleRejectClick = useCallback((hotspot) => {
    // Open rejection modal instead of rejecting directly
    setHotspotToReject(hotspot);
    setRejectionModal(true);
    setDetailModal(false); // Close detail modal
  }, []);

  const handleSuspendClick = useCallback((hotspot) => {
    // Open suspend modal
    setHotspotToSuspend(hotspot);
    setSuspendModal(true);
    setDetailModal(false); // Close detail modal
  }, []);

  const handleVerify = useCallback(async (hotspot) => {
    if (!hotspot || !hotspot.tag_id) {
      alert("Tag ID is required for verification");
      return;
    }

    try {
      await updateTagVerification({
        tag_id: hotspot.tag_id,
        is_verified: true,
      });

      // Refresh the list to show updated status
      await fetchList();
      
      // Close the modal
      setDetailModal(false);
      setSelectedHotspot(null);
    } catch (error) {
      console.error("Error verifying tag:", error);
      alert(error.response?.data?.message || error.message || "Failed to verify tag");
    }
  }, [fetchList]);

  const handleViewUserInfo = useCallback((userId) => {
    setSelectedUserId(userId);
    setUserInfoModal(true);
  }, []);

  const handleSuspendConfirm = useCallback(async (reason) => {
    if (!hotspotToSuspend || !hotspotToSuspend.tag_id) {
      throw new Error("Tag ID is required for suspension");
    }

    try {
      await suspendHotspot({
        tag_id: hotspotToSuspend.tag_id,
        reason: reason,
      });

      // Refresh the list
      await fetchList();
      
      // Close modals
      setSuspendModal(false);
      setHotspotToSuspend(null);
    } catch (error) {
      console.error("Error suspending hotspot:", error);
      throw error;
    }
  }, [hotspotToSuspend, fetchList]);

  const handleRejectConfirm = useCallback(async (hotspot, rejectionReason) => {
    try {
      // Call the reject API with rejection reason
      await rejectHotspot({
        request_id: hotspot.request_id,
        rejection_reason: rejectionReason,
      });
      
      // Optimistically remove from list
      setData((prevData) =>
        prevData.filter((item) => item.request_id !== hotspot.request_id)
      );
      setTotalRows((prev) => Math.max(0, prev - 1));
      
      // Refetch to ensure consistency
      await fetchList();
      setRejectionModal(false);
      setHotspotToReject(null);
      setDetailModal(false);
      setSelectedHotspot(null);
    } catch (error) {
      console.error("Error rejecting hotspot:", error);
      // Show error message
      throw new Error(error.response?.data?.message || error.message || "Failed to reject hotspot");
    }
  }, [fetchList]);

  const handleFilterChange = useCallback((filterType, value) => {
    setCurrentPage(1);
    
    switch (filterType) {
      case "status":
        setStatus(value);
        break;
      case "type":
        setType(value);
        break;
      case "dateRange":
        setDateRange(value);
        if (value) {
          setStartDate("");
          setEndDate("");
        }
        break;
      case "startDate":
        setStartDate(value);
        if (value) {
          setDateRange("");
        }
        break;
      case "endDate":
        setEndDate(value);
        if (value) {
          setDateRange("");
        }
        break;
      case "isSuspended":
        setIsSuspended(value);
        break;
      default:
        break;
    }
  }, []);

  const clearFilters = useCallback(() => {
    setStatus("");
    setType("");
    setKeyword("");
    setDateRange("");
    setStartDate("");
    setEndDate("");
    setIsSuspended("");
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handlePerRowsChange = useCallback((newPerPage, page) => {
    setCurrentPage(page);
  }, []);

  // Single effect to handle all filter changes (excluding keyword)
  useEffect(() => {
    // Create filter key to detect changes
    const filterKey = JSON.stringify({
      status,
      type,
      dateRange,
      startDate,
      endDate,
      isSuspended,
      currentPage,
    });

    // Skip if filters haven't changed (prevents initial double call)
    if (previousFiltersRef.current === filterKey) {
      return;
    }

    // Update previous filters
    previousFiltersRef.current = filterKey;

    // Fetch data
    fetchList();
  }, [status, type, dateRange, startDate, endDate, isSuspended, currentPage, fetchList]);

  // Debounced keyword search (1 second delay)
  useEffect(() => {
    // Clear existing timeout
    if (keywordTimeoutRef.current) {
      clearTimeout(keywordTimeoutRef.current);
    }

    // Set new timeout for 1 second
    keywordTimeoutRef.current = setTimeout(() => {
      fetchList(keyword);
    }, 1000);

    // Cleanup timeout
    return () => {
      if (keywordTimeoutRef.current) {
        clearTimeout(keywordTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]); // Don't include fetchList to avoid unnecessary re-runs

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (keywordTimeoutRef.current) {
        clearTimeout(keywordTimeoutRef.current);
      }
    };
  }, []);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Dashboard" breadcrumbItem="Hotspot Moderation" />

          <Col lg={12}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">Hotspot Moderation Filters</h4>
              </CardHeader>

              <CardBody>
                <Row className="g-3 mb-3">
                  <Col md={3}>
                    <FormGroup>
                      <Label>Status</Label>
                      <Input
                        type="select"
                        value={status}
                        onChange={(e) => handleFilterChange("status", e.target.value)}
                      >
                        <option value="">All</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </Input>
                    </FormGroup>
                  </Col>

                  <Col md={3}>
                    <FormGroup>
                      <Label>Type</Label>
                      <Input
                        type="select"
                        value={type}
                        onChange={(e) => handleFilterChange("type", e.target.value)}
                      >
                        <option value="">All</option>
                        <option value="personal">Personal</option>
                        <option value="community">Community</option>
                      </Input>
                    </FormGroup>
                  </Col>

                  <Col md={3}>
                    <FormGroup>
                      <Label>Date Range</Label>
                      <Input
                        type="select"
                        value={dateRange}
                        onChange={(e) => handleFilterChange("dateRange", e.target.value)}
                      >
                        <option value="">None</option>
                        <option value="last_24_hours">Last 24 Hours</option>
                        <option value="last_7_days">Last 7 Days</option>
                        <option value="last_30_days">Last 30 Days</option>
                      </Input>
                    </FormGroup>
                  </Col>

                  <Col md={3}>
                    <FormGroup>
                      <Label>Keyword Search</Label>
                      <Input
                        type="text"
                        placeholder="Search..."
                        value={keyword}
                        onChange={(e) => handleKeywordChange(e.target.value)}
                      />
                    </FormGroup>
                  </Col>

                  <Col md={3}>
                    <FormGroup>
                      <Label>Is Suspended</Label>
                      <Input
                        type="select"
                        value={isSuspended}
                        onChange={(e) => handleFilterChange("isSuspended", e.target.value)}
                      >
                        <option value="">All</option>
                        <option value="true">Suspended</option>
                        <option value="false">Not Suspended</option>
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>

                <Row className="g-3 mb-3">
                  <Col md={4}>
                    <FormGroup>
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => handleFilterChange("startDate", e.target.value)}
                      />
                    </FormGroup>
                  </Col>

                  <Col md={4}>
                    <FormGroup>
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={endDate}
                        onChange={(e) => handleFilterChange("endDate", e.target.value)}
                      />
                    </FormGroup>
                  </Col>

                  <Col md={4} className="d-flex align-items-end">
                    <Button color="secondary" onClick={clearFilters}>
                      Clear Filters
                    </Button>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                {/* <h4 className="card-title mb-0">Hotspot Moderation</h4> */}
              </CardHeader>

              <CardBody>
                <div id="customerList">
                  <DataTable
                    columns={columns}
                    data={data}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                    paginationPerPage={limit}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    progressPending={loading}
                    highlightOnHover
                    noDataComponent="No hotspots found"
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Container>
      </div>

      <HotspotDetailModal
        isOpen={detailModal}
        toggle={toggleDetailModal}
        hotspotData={selectedHotspot}
        onApprove={handleApprove}
        onReject={handleRejectClick}
        onSuspend={handleSuspendClick}
        onVerify={handleVerify}
        onViewUserInfo={handleViewUserInfo}
        loading={loadingTagDetails}
      />

      <RejectionModal
        isOpen={rejectionModal}
        toggle={() => {
          setRejectionModal(false);
          setHotspotToReject(null);
        }}
        hotspot={hotspotToReject}
        onConfirm={handleRejectConfirm}
      />

      <SuspendModal
        isOpen={suspendModal}
        toggle={() => {
          setSuspendModal(false);
          setHotspotToSuspend(null);
        }}
        tagId={hotspotToSuspend?.tag_id}
        tagName={hotspotToSuspend?.tag_name}
        onConfirm={handleSuspendConfirm}
      />

      <UserInfoModal
        isOpen={userInfoModal}
        toggle={() => {
          setUserInfoModal(false);
          setSelectedUserId(null);
        }}
        userId={selectedUserId}
        onUpdate={() => {
          // Optionally refresh the list after user action
          fetchList();
        }}
      />
    </React.Fragment>
  );
};

export default HotspotModerationPage;
